/**
 * Shopify Bundle app personalization inherit — pure merge helpers (PB-SBI-1).
 * Browser: window.PBShopifyBundleInherit · Node: module.exports (vm tests).
 */
(function (global) {
  'use strict';

  function normalizeShopifyBundleFieldLabel(label) {
    var sync = global.PBOfferBundleSync;
    if (sync && typeof sync.normalizeBundleFieldLabel === 'function') {
      return sync.normalizeBundleFieldLabel(label);
    }
    return String(label == null ? '' : label).trim().toLowerCase().replace(/\s+/g, ' ');
  }

  /**
   * Union component + parent personalization fields by normalized label.
   * First-seen field metadata wins. Empty component lists are skipped.
   *
   * @param {Array<Array<object>>} componentFieldLists
   * @param {Array<object>} parentFieldList
   * @returns {Array<object>}
   */
  function mergeShopifyBundlePersonalization(componentFieldLists, parentFieldList) {
    var byLabel = new Map();
    var lists = Array.isArray(componentFieldLists) ? componentFieldLists : [];
    var parent = Array.isArray(parentFieldList) ? parentFieldList : [];

    lists.concat([parent]).forEach(function (fields) {
      if (!Array.isArray(fields) || fields.length === 0) return;
      fields.forEach(function (field) {
        if (!field || field.label == null || String(field.label).trim() === '') return;
        var key = normalizeShopifyBundleFieldLabel(field.label);
        if (!key) return;
        if (!byLabel.has(key)) byLabel.set(key, field);
      });
    });

    return Array.from(byLabel.values());
  }

  function jaccardShopifyBundleLabelSets(a, b) {
    var A = new Set(a);
    var B = new Set(b);
    var inter = 0;
    A.forEach(function (x) {
      if (B.has(x)) inter += 1;
    });
    var union = A.size + B.size - inter;
    return union === 0 ? 1 : inter / union;
  }

  /**
   * Pack when one non-empty component list, or every pair has Jaccard label similarity ≥ 0.6.
   * @param {Array<Array<object>>} componentFieldLists
   * @returns {'pack'|'mixed'}
   */
  function classifyShopifyBundleMergeMode(componentFieldLists) {
    var sets = (Array.isArray(componentFieldLists) ? componentFieldLists : [])
      .filter(function (list) {
        return Array.isArray(list) && list.length > 0;
      })
      .map(function (list) {
        return list
          .map(function (f) {
            return normalizeShopifyBundleFieldLabel(f && f.label);
          })
          .filter(Boolean);
      });
    if (sets.length <= 1) return 'pack';
    var min = 1;
    for (var i = 0; i < sets.length; i++) {
      for (var j = i + 1; j < sets.length; j++) {
        min = Math.min(min, jaccardShopifyBundleLabelSets(sets[i], sets[j]));
      }
    }
    return min >= 0.6 ? 'pack' : 'mixed';
  }

  /**
   * Planned customer field list — pack merges by label; mixed keeps per-component fields + provenance.
   *
   * @param {Array<{ id?, handle?, title?, personalizationFields? }>} components
   * @param {Array<object>} parentFields
   * @returns {{ mode: 'pack'|'mixed', fields: Array<object> }}
   */
  function buildShopifyBundleFieldPlan(components, parentFields) {
    var comps = Array.isArray(components) ? components : [];
    var parent = Array.isArray(parentFields) ? parentFields : [];
    var componentFieldLists = comps.map(function (c) {
      return c && Array.isArray(c.personalizationFields) ? c.personalizationFields : [];
    });
    var mode = classifyShopifyBundleMergeMode(componentFieldLists);

    if (mode === 'pack') {
      return {
        mode: 'pack',
        fields: mergeShopifyBundlePersonalization(componentFieldLists, parent)
      };
    }

    var fields = [];
    comps.forEach(function (component) {
      if (!component) return;
      var list = Array.isArray(component.personalizationFields)
        ? component.personalizationFields
        : [];
      var productId = component.id != null ? String(component.id) : '';
      var handle = component.handle != null ? String(component.handle) : '';
      var title = component.title != null ? String(component.title) : '';
      var image = component.image != null ? String(component.image) : '';
      list.forEach(function (field) {
        if (!field || field.label == null || String(field.label).trim() === '') return;
        var entry = Object.assign({}, field);
        entry.provenance = {
          productId: productId,
          handle: handle,
          title: title,
          image: image,
          label: field.label
        };
        fields.push(entry);
      });
    });

    parent.forEach(function (field) {
      if (!field || field.label == null || String(field.label).trim() === '') return;
      var entry = Object.assign({}, field);
      entry.provenance = {
        productId: null,
        handle: '',
        title: '',
        label: field.label
      };
      fields.push(entry);
    });

    return { mode: 'mixed', fields: fields };
  }

  /** True when BUILDER_DATA carries Shopify bundle inherit markers. */
  function isShopifyBundleProductShape(data) {
    if (!data || typeof data !== 'object') return false;
    if (data.shopifyBundle !== true) return false;
    return Array.isArray(data.shopifyBundleComponents);
  }

  /**
   * Stable builder-state key — mixed mode scopes by productId + label so colliding labels do not overwrite.
   * @param {object} field
   * @param {'pack'|'mixed'} mergeMode
   * @returns {string}
   */
  function resolveShopifyBundleFieldSelectionKey(field, mergeMode) {
    if (!field || field.label == null) return '';
    var displayLabel = String(field.label).trim();
    if (mergeMode !== 'mixed') return displayLabel;
    var prov = field.provenance;
    if (!prov) return displayLabel;
    var norm = normalizeShopifyBundleFieldLabel(prov.label != null ? prov.label : displayLabel);
    if (prov.productId != null && String(prov.productId) !== '') {
      return String(prov.productId) + '::' + norm;
    }
    return '__parent__::' + norm;
  }

  /**
   * PB-SBT-1 — alias index consumed by the shared selection lookup in pb-builder-tokens.
   *
   * Maps the merchant-authored label (`name`) to the state keys that actually hold answers
   * (`123::name`), tagged with the owning component so `{Name}` in one component's copy
   * cannot pick up another component's answer. Empty in pack mode and on non-bundle
   * products — those already store answers under the plain label.
   *
   * @param {{ mode: 'pack'|'mixed', fields: Array<object> }} plan
   * @returns {Object<string, Array<{ key: string, productId: string }>>}
   */
  function buildShopifyBundleAliasIndex(plan) {
    var index = {};
    if (!plan || plan.mode !== 'mixed' || !Array.isArray(plan.fields)) return index;

    plan.fields.forEach(function (field) {
      if (!field || field.label == null) return;
      var key = resolveShopifyBundleFieldSelectionKey(field, 'mixed');
      if (!key) return;
      var prov = field.provenance || null;
      var productId = prov && prov.productId != null ? String(prov.productId) : '';
      var aliases = [normalizeShopifyBundleFieldLabel(field.label)];
      if (prov && prov.label != null) {
        aliases.push(normalizeShopifyBundleFieldLabel(prov.label));
      }
      aliases.forEach(function (alias) {
        if (!alias || alias === key) return;
        var entries = index[alias] || (index[alias] = []);
        var exists = entries.some(function (entry) {
          return entry.key === key;
        });
        if (!exists) entries.push({ key: key, productId: productId });
      });
    });

    return index;
  }

  /**
   * Merge component + parent personalization into mainProduct at boot.
   * Uses smart field plan (pack vs mixed). Idempotent unless force is true.
   *
   * @param {boolean} [force]
   * @returns {object|null}
   */
  function applyShopifyBundleMergeSync(force) {
    var data = global.BUILDER_DATA;
    if (!data || data.shopifyBundle !== true) return data || null;

    if (force) {
      data._pbShopifyBundleMergeApplied = false;
    } else if (data._pbShopifyBundleMergeApplied) {
      return data;
    }

    var components = Array.isArray(data.shopifyBundleComponents) ? data.shopifyBundleComponents : [];
    var main = data.mainProduct;
    var parentFields =
      main && Array.isArray(main.personalization) ? main.personalization : [];
    var plan = buildShopifyBundleFieldPlan(components, parentFields);
    var merged = plan.fields;

    if (main) {
      main.personalization = merged;
      main.config = merged.slice();
    }
    data.shopifyBundleFieldPlan = plan;
    data.shopifyBundleMergeMode = plan.mode;
    data.shopifyBundleMergedPersonalization = merged;
    data.shopifyBundleAliasIndex = buildShopifyBundleAliasIndex(plan);
    data._pbShopifyBundleMergeApplied = true;
    return data;
  }

  function isShopifyBundleMode(data) {
    return !!(data && data.shopifyBundle === true);
  }

  function countMergedPersonalizationFields(data) {
    if (!data) return 0;
    var merged = data.shopifyBundleMergedPersonalization;
    if (!Array.isArray(merged) && data.mainProduct) {
      merged = data.mainProduct.config || data.mainProduct.personalization;
    }
    return Array.isArray(merged) ? merged.length : 0;
  }

  /**
   * Main-flow step kinds for shopify-bundle mode (no offer step).
   * @param {object} data BUILDER_DATA after merge
   * @param {object} navHelpers countVariantSteps, hasAnyAddonsFromData
   * @returns {string[]}
   */
  function buildShopifyBundleStepKinds(data, navHelpers) {
    var kinds = [];
    var countVariants =
      navHelpers && typeof navHelpers.countVariantSteps === 'function'
        ? navHelpers.countVariantSteps(data)
        : 0;
    var p13n = countMergedPersonalizationFields(data);
    var hasAddons =
      navHelpers && typeof navHelpers.hasAnyAddonsFromData === 'function'
        ? navHelpers.hasAnyAddonsFromData(data)
        : false;
    var i;
    for (i = 0; i < p13n; i += 1) kinds.push('personalize');
    for (i = 0; i < countVariants; i += 1) kinds.push('variant');
    if (hasAddons) kinds.push('addon');
    return kinds;
  }

  /**
   * Add-ons-only bundle: land on add-ons when no merged personalization fields.
   * @param {object} data
   * @param {object} navHelpers
   * @returns {number} 1-based step index
   */
  function resolveShopifyBundleOpenStep(data, navHelpers) {
    if (!isShopifyBundleMode(data)) return 1;
    var countVariants =
      navHelpers && typeof navHelpers.countVariantSteps === 'function'
        ? navHelpers.countVariantSteps(data)
        : 0;
    var p13n = countMergedPersonalizationFields(data);
    if (p13n > 0) return 1;
    var hasAddons =
      navHelpers && typeof navHelpers.hasAnyAddonsFromData === 'function'
        ? navHelpers.hasAnyAddonsFromData(data)
        : false;
    if (!hasAddons) return 1;
    return countVariants + 1;
  }

  /**
   * Cold-init: skip empty personalization for add-ons-only shopify bundles.
   * @param {object} [pb] productBuilder
   * @returns {boolean}
   */
  /**
   * Parent bundle variant — same resolution as main-product finalize in pb-logic-offers.
   * @param {object} builderState
   * @returns {object|null}
   */
  function resolveShopifyBundleParentVariant(builderState) {
    var s = builderState;
    if (!s || !s.main || !Array.isArray(s.main.variants) || !s.main.variants.length) {
      return null;
    }
    var variants = s.main.variants;
    var options = s.main.options || [];
    var selections = s.main.selections || {};
    var matched = variants.find(function (v) {
      return options.every(function (opt, idx) {
        return v.options[idx] === selections[opt.name];
      });
    });
    if (matched) return matched;
    var available = variants.find(function (v) {
      return v.available;
    });
    return available || variants[0];
  }

  /**
   * Product ids for Shopify bundle components — must not appear as main cart line id.
   * @param {object} builderData
   * @returns {Set<string>}
   */
  function collectShopifyBundleComponentProductIds(builderData) {
    var ids = new Set();
    var components = builderData && Array.isArray(builderData.shopifyBundleComponents)
      ? builderData.shopifyBundleComponents
      : [];
    components.forEach(function (c) {
      if (c && c.id != null) ids.add(String(c.id));
    });
    return ids;
  }

  var PB_BUNDLE_COMPONENTS_KEY = '_pb_bundle_components';
  var PB_BUNDLE_COMPONENTS_SCHEMA_V = 1;

  /**
   * Normalized labels that appear on more than one planned field (mixed collision set).
   * @param {Array<object>} fields
   * @returns {Set<string>}
   */
  function collectShopifyBundleCollidingLabels(fields) {
    var counts = new Map();
    (fields || []).forEach(function (field) {
      if (!field || field.label == null) return;
      var key = normalizeShopifyBundleFieldLabel(field.label);
      if (!key) return;
      counts.set(key, (counts.get(key) || 0) + 1);
    });
    var colliding = new Set();
    counts.forEach(function (count, key) {
      if (count > 1) colliding.add(key);
    });
    return colliding;
  }

  /**
   * Read builder selection for a planned field (scoped key in mixed mode).
   * @param {object} selections
   * @param {object} field
   * @param {'pack'|'mixed'} mergeMode
   * @returns {*}
   */
  function readShopifyBundleFieldSelectionValue(selections, field, mergeMode) {
    if (!selections || !field) return undefined;
    var key = resolveShopifyBundleFieldSelectionKey(field, mergeMode);
    if (key && selections[key] != null && selections[key] !== '') {
      return selections[key];
    }
    if (field.label != null && selections[field.label] != null && selections[field.label] !== '') {
      return selections[field.label];
    }
    return undefined;
  }

  /**
   * PB-MF-1 — answer-book lookup for Finish readiness (scoped key in mixed mode).
   * @param {object} field
   * @param {object} [selections]
   * @param {'pack'|'mixed'} [mergeMode]
   * @returns {{ selectionKey: string, displayLabel: string, hasKey: boolean, value: * }}
   */
  function resolveShopifyBundleSelectionLookup(field, selections, mergeMode) {
    if (!field) {
      return { selectionKey: '', displayLabel: 'field', hasKey: false, value: undefined };
    }
    var mode = mergeMode || (field.provenance ? 'mixed' : 'pack');
    var displayLabel =
      field.displayLabel != null
        ? String(field.displayLabel).trim()
        : String(field.label || 'field').trim();
    var selectionKey = resolveShopifyBundleFieldSelectionKey(field, mode);
    if (!selectionKey && field.label != null) selectionKey = String(field.label).trim();
    var sel = selections || {};
    var hasKey = Object.prototype.hasOwnProperty.call(sel, selectionKey);
    return {
      selectionKey: selectionKey,
      displayLabel: displayLabel || 'field',
      hasKey: hasKey,
      value: hasKey ? sel[selectionKey] : undefined,
    };
  }

  /**
   * PB-MF-4 — DOM / aiImageByLabel lookup keys for a planned field (scoped + display labels).
   * @param {object} field
   * @param {'pack'|'mixed'} [mergeMode]
   * @returns {string[]}
   */
  function resolveShopifyBundleFieldIdentityKeys(field, mergeMode) {
    if (!field) return [];
    var mode = mergeMode || (field.provenance ? 'mixed' : 'pack');
    var keys = [];
    function push(key) {
      if (key && keys.indexOf(key) === -1) keys.push(key);
    }
    push(resolveShopifyBundleFieldSelectionKey(field, mode));
    if (field.displayLabel != null) push(String(field.displayLabel).trim());
    if (field.label != null) push(String(field.label).trim());
    return keys;
  }

  /**
   * PB-MF-4 — media answer-book lookup (scoped key + legacy flat label when provenance exists).
   * @param {object} field
   * @param {object} [selections]
   * @param {'pack'|'mixed'} [mergeMode]
   * @returns {{ selectionKey: string, displayLabel: string, hasKey: boolean, value: *, legacyFlat?: boolean }}
   */
  function resolveShopifyBundleMediaSelectionLookup(field, selections, mergeMode) {
    var base = resolveShopifyBundleSelectionLookup(field, selections, mergeMode);
    if (base.hasKey && base.value != null && base.value !== '') return base;
    var mode = mergeMode || (field.provenance ? 'mixed' : 'pack');
    if (mode !== 'mixed' || !field.provenance || field.label == null) return base;
    var flatLabel = String(field.label).trim();
    var sel = selections || {};
    if (
      flatLabel &&
      flatLabel !== base.selectionKey &&
      Object.prototype.hasOwnProperty.call(sel, flatLabel)
    ) {
      var flatVal = sel[flatLabel];
      if (flatVal != null && flatVal !== '') {
        return {
          selectionKey: base.selectionKey,
          displayLabel: base.displayLabel,
          hasKey: true,
          value: flatVal,
          legacyFlat: true,
        };
      }
    }
    return base;
  }

  /**
   * Customer-visible property key — prefix with component title only on label collision.
   * @param {object} field
   * @param {'pack'|'mixed'} mergeMode
   * @param {Set<string>} collidingLabels
   * @param {string} [parentTitle]
   * @returns {string}
   */
  function resolveShopifyBundleVisiblePropertyKey(field, mergeMode, collidingLabels, parentTitle) {
    if (!field || field.label == null) return '';
    var soloLabel = String(field.label).trim();
    if (mergeMode === 'pack') return soloLabel;
    var norm = normalizeShopifyBundleFieldLabel(soloLabel);
    if (!collidingLabels.has(norm)) return soloLabel;
    var prov = field.provenance || {};
    var prefix = prov.title && String(prov.title).trim();
    if (!prefix && (prov.productId == null || String(prov.productId) === '') && parentTitle) {
      prefix = String(parentTitle).trim();
    }
    return prefix ? prefix + ' - ' + soloLabel : soloLabel;
  }

  /**
   * @param {Array<object>} components
   * @param {string|null|undefined} productId
   * @param {string|null|undefined} handle
   * @returns {object|null}
   */
  function findShopifyBundleComponentEntry(components, productId, handle) {
    if (!Array.isArray(components)) return null;
    var pid = productId != null ? String(productId) : '';
    var h = handle != null ? String(handle) : '';
    return (
      components.find(function (c) {
        if (!c) return false;
        if (pid && c.productId != null && String(c.productId) === pid) return true;
        if (h && c.handle != null && String(c.handle) === h) return true;
        return false;
      }) || null
    );
  }

  /**
   * Parse `_pb_bundle_components` JSON v1.
   * @param {string|object|null|undefined} raw
   * @returns {object|null}
   */
  function parseShopifyBundleComponentsMap(raw) {
    if (raw == null || raw === '') return null;
    if (typeof raw === 'object' && raw.v === PB_BUNDLE_COMPONENTS_SCHEMA_V) return raw;
    if (typeof raw !== 'string') return null;
    try {
      var parsed = JSON.parse(raw);
      if (parsed && parsed.v === PB_BUNDLE_COMPONENTS_SCHEMA_V) return parsed;
    } catch (e) {
      return null;
    }
    return null;
  }

  /**
   * Build scoped selections from parsed component map (mixed mode).
   * @param {object} map
   * @param {{ mode?: string, fields?: Array<object> }} plan
   * @returns {object}
   */
  function hydrateShopifyBundleSelectionsFromMap(map, plan) {
    var selections = {};
    if (!map || map.v !== PB_BUNDLE_COMPONENTS_SCHEMA_V || !plan || plan.mode !== 'mixed') {
      return selections;
    }
    (plan.fields || []).forEach(function (field) {
      var prov = field.provenance;
      if (!prov) return;
      var component = findShopifyBundleComponentEntry(map.components, prov.productId, prov.handle);
      if (!component || !component.properties) return;
      var soloLabel = field.label != null ? String(field.label).trim() : '';
      if (!soloLabel) return;
      var value = component.properties[soloLabel];
      if (value == null || value === '') return;
      selections[resolveShopifyBundleFieldSelectionKey(field, 'mixed')] = value;
    });
    return selections;
  }

  /**
   * Resolve field plan from BUILDER_DATA (applies merge sync when needed).
   * @param {object} builderData
   * @returns {{ mode: 'pack'|'mixed', fields: Array<object> }}
   */
  function resolveShopifyBundleFieldPlanFromData(builderData) {
    if (builderData && builderData.shopifyBundleFieldPlan) {
      return builderData.shopifyBundleFieldPlan;
    }
    if (builderData && builderData.shopifyBundle === true) {
      var components = Array.isArray(builderData.shopifyBundleComponents)
        ? builderData.shopifyBundleComponents
        : [];
      var parentFields =
        builderData.mainProduct && Array.isArray(builderData.mainProduct.personalization)
          ? builderData.mainProduct.personalization
          : [];
      var builtPlan = buildShopifyBundleFieldPlan(components, parentFields);
      builderData.shopifyBundleFieldPlan = builtPlan;
      builderData.shopifyBundleMergeMode = builtPlan.mode;
      builderData.shopifyBundleMergedPersonalization = builtPlan.fields;
      if (builderData.mainProduct) {
        builderData.mainProduct.personalization = builtPlan.fields;
        builderData.mainProduct.config = builtPlan.fields.slice();
      }
      builderData._pbShopifyBundleMergeApplied = true;
      return builtPlan;
    }
    applyShopifyBundleMergeSync(false);
    var globalData = global.BUILDER_DATA;
    if (globalData && globalData.shopifyBundleFieldPlan) {
      return globalData.shopifyBundleFieldPlan;
    }
    return { mode: 'pack', fields: [] };
  }

  /**
   * Encode visible collision-aware props + hidden `_pb_bundle_components` v1 (PB-SBM-4).
   * @param {object} params
   * @param {object} params.builderData
   * @param {object} params.selections
   * @param {object} [params.extra]
   * @param {{ mode: 'pack'|'mixed', fields: Array<object> }} params.plan
   * @returns {{ properties: object, map: object|null }}
   */
  function encodeShopifyBundleComponentsMap(params) {
    var builderData = params && params.builderData;
    var selections = (params && params.selections) || {};
    var extra = (params && params.extra) || {};
    var plan = params && params.plan;
    if (!plan || !builderData) return { properties: Object.assign({}, extra), map: null };

    var mergeMode = plan.mode;
    var fields = visiblePersonalizationFields(plan.fields);
    var colliding = collectShopifyBundleCollidingLabels(fields);
    var mainProduct = builderData.mainProduct || {};
    var parentTitle = mainProduct.title != null ? String(mainProduct.title) : '';
    var parentProductId = mainProduct.id != null ? String(mainProduct.id) : '';
    var parentHandle = mainProduct.handle != null ? String(mainProduct.handle) : '';
    var visible = Object.assign({}, extra);
    var componentBags = new Map();

    function ensureComponentBag(prov) {
      if (!prov || prov.productId == null || String(prov.productId) === '') return null;
      var pid = String(prov.productId);
      if (!componentBags.has(pid)) {
        componentBags.set(pid, {
          productId: pid,
          handle: prov.handle != null ? String(prov.handle) : '',
          title: prov.title != null ? String(prov.title) : '',
          properties: {}
        });
      }
      return componentBags.get(pid);
    }

    fields.forEach(function (field) {
      var value = readShopifyBundleFieldSelectionValue(selections, field, mergeMode);
      if (value == null || value === '') return;
      var soloLabel = String(field.label).trim();
      var visibleKey = resolveShopifyBundleVisiblePropertyKey(
        field,
        mergeMode,
        colliding,
        parentTitle
      );
      if (visibleKey) visible[visibleKey] = value;

      if (mergeMode === 'mixed') {
        var prov = field.provenance;
        if (prov && prov.productId != null && String(prov.productId) !== '') {
          var bag = ensureComponentBag(prov);
          if (bag) bag.properties[soloLabel] = value;
        }
      }
    });

    // TopicMulti stores child answers by question, not by the parent field label.
    // Use the same codec as solo products, scoped to each component's field plan.
    var topicGroups = new Map();
    fields.filter(function(f) { return String(f.type).includes('TopicMulti'); }).forEach(function(field) {
      var id = mergeMode === 'mixed' && field.provenance ? String(field.provenance.productId) : '';
      if (!topicGroups.has(id)) topicGroups.set(id, []);
      topicGroups.get(id).push(field);
    });
    var pb = (params && params.pb) || global.productBuilder;
    topicGroups.forEach(function(group) {
      if (!pb || typeof pb.encodeTopicMultiProperties !== 'function') throw new Error('Topic answers are not ready. Please reopen personalization.');
      var bag = mergeMode === 'mixed' ? ensureComponentBag(group[0].provenance) : null;
      // Resolve {Dedicated to} and other tokens within this component, without leaking aliases.
      var componentSelections = Object.assign({}, selections, bag ? bag.properties : {});
      var topicProps = pb.encodeTopicMultiProperties({ selections: componentSelections, topicSelections: params.topicSelections || {} }, group.map(function(f) { return normalizeFieldForMount(f, mergeMode); })).properties;
      Object.keys(topicProps).forEach(function(key) {
        var visibleKey = topicGroups.size > 1 && bag ? bag.title + ' - ' + key : key;
        visible[visibleKey] = topicProps[key];
        if (bag) bag.properties[key] = topicProps[key];
      });
    });

    var map = null;
    if (mergeMode === 'mixed' && componentBags.size > 0) {
      map = {
        v: PB_BUNDLE_COMPONENTS_SCHEMA_V,
        mode: 'mixed',
        parentProductId: parentProductId,
        parentHandle: parentHandle,
        components: Array.from(componentBags.values())
      };
      visible[PB_BUNDLE_COMPONENTS_KEY] = JSON.stringify(map);
    } else if (mergeMode === 'pack') {
      var comps = Array.isArray(builderData.shopifyBundleComponents)
        ? builderData.shopifyBundleComponents
        : [];
      var packComponents = comps
        .map(function (c) {
          var bag = {
            productId: c.id != null ? String(c.id) : '',
            handle: c.handle != null ? String(c.handle) : '',
            title: c.title != null ? String(c.title) : '',
            properties: {}
          };
          var compFields = visiblePersonalizationFields(c.personalizationFields || []);
          compFields.forEach(function (field) {
            var val = readShopifyBundleFieldSelectionValue(selections, field, 'pack');
            if (val == null || val === '') return;
            bag.properties[String(field.label).trim()] = val;
          });
          return bag;
        })
        .filter(function (c) {
          return Object.keys(c.properties).length > 0;
        });
      if (packComponents.length) {
        map = {
          v: PB_BUNDLE_COMPONENTS_SCHEMA_V,
          mode: 'pack',
          parentProductId: parentProductId,
          parentHandle: parentHandle,
          components: packComponents
        };
      }
    }

    return { properties: visible, map: map };
  }

  /**
   * Single parent bundle cart line for Shopify Bundle PDP finalize (PB-SBI-4).
   * Encodes merged personalization onto the parent variant — no component fan-out.
   *
   * @param {object} params
   * @param {object} params.pb productBuilder (encodeCartLineProperties)
   * @param {object} params.builderState
   * @param {object} params.builderData BUILDER_DATA
   * @param {string} [params.addonString] Add-ons property value for parent line
   * @returns {object|null}
   */
  function buildShopifyBundleMainCartItem(params) {
    var pb = params && params.pb;
    var builderState = params && params.builderState;
    var builderData = params && params.builderData;
    if (!isShopifyBundleMode(builderData)) return null;
    var mainProduct = builderData.mainProduct;
    if (!mainProduct || !builderState || !builderState.main) return null;

    var mainVariant = resolveShopifyBundleParentVariant(builderState);
    if (!mainVariant || mainVariant.id == null) return null;

    var s = builderState;
    var mainStateSlice = {
      selections: s.main.selections,
      topicSelections: s.main.topicSelections,
      variantId: mainVariant.id,
      catalogDesignLibrary: s.catalogDesignLibrary
    };
    var extra = params.addonString ? { 'Add-ons': params.addonString } : {};
    var plan = resolveShopifyBundleFieldPlanFromData(builderData);
    var mainProps = {};

    if (plan.mode === 'mixed') {
      var mixedEncoded = encodeShopifyBundleComponentsMap({
        pb: pb,
        builderData: builderData,
        topicSelections: s.main.topicSelections,
        selections: s.main.selections,
        extra: extra,
        plan: plan
      });
      mainProps = mixedEncoded.properties;
    } else if (pb && typeof pb.encodeCartLineProperties === 'function') {
      mainProps = pb.encodeCartLineProperties(
        mainStateSlice,
        s.main.options,
        extra,
        { fieldItem: mainProduct }
      );
      var packEncoded = encodeShopifyBundleComponentsMap({
        pb: pb,
        builderData: builderData,
        topicSelections: s.main.topicSelections,
        selections: s.main.selections,
        extra: {},
        plan: plan
      });
      if (packEncoded.map) {
        mainProps[PB_BUNDLE_COMPONENTS_KEY] = JSON.stringify(packEncoded.map);
      }
    } else {
      var fallbackEncoded = encodeShopifyBundleComponentsMap({
        pb: pb,
        builderData: builderData,
        topicSelections: s.main.topicSelections,
        selections: s.main.selections,
        extra: extra,
        plan: plan
      });
      mainProps = fallbackEncoded.properties;
    }

    var sessionOverlay = null;
    if (pb && typeof pb.buildCartLineSessionOverlay === 'function') {
      sessionOverlay = pb.buildCartLineSessionOverlay(
        s.main.selections,
        s.main.topicSelections,
        s.catalogDesignLibrary
      );
    }

    return {
      id: mainVariant.id,
      quantity: 1,
      properties: mainProps,
      __pbSessionState: sessionOverlay
    };
  }

  /**
   * True when cart/add items use one parent bundle line (component ids not on main line).
   * @param {Array<object>} items cart rows before Shopify fetch strip
   * @param {object} builderData
   * @param {object} [builderState]
   * @returns {boolean}
   */
  /**
   * Cart edit open — ensure merged fields + no offer-bundle fan-out state (PB-SBI-5).
   * @param {object} builderData
   * @param {object} [builderState]
   * @returns {boolean}
   */
  function prepareShopifyBundleCartEditState(builderData, builderState) {
    if (!isShopifyBundleMode(builderData)) return false;
    applyShopifyBundleMergeSync(true);
    if (builderState) {
      builderState.bundle = builderState.bundle || { selectedId: null, configurations: {} };
      builderState.bundle.selectedId = null;
      builderState.bundle.configurations = builderState.bundle.configurations || {};
    }
    return true;
  }

  /**
   * Decode parent bundle line properties into merged personalization state for cart edit hydrate.
   * @param {object} params
   * @param {object} params.pb productBuilder
   * @param {object} params.cartLine Shopify cart line
   * @param {object} params.builderData BUILDER_DATA after merge sync
   * @returns {object|null} { selections, topicSelections, variantId }
   */
  function hydrateShopifyBundleCartEditFromLine(params) {
    var pb = params && params.pb;
    var cartLine = params && params.cartLine;
    var builderData = params && params.builderData;
    if (!isShopifyBundleMode(builderData) || !cartLine) return null;
    var plan = resolveShopifyBundleFieldPlanFromData(builderData);
    var mainProduct = builderData.mainProduct;
    if (!mainProduct || !pb) return null;

    var fields =
      builderData.shopifyBundleMergedPersonalization ||
      mainProduct.config ||
      mainProduct.personalization ||
      [];
    var options = builderData.options || mainProduct.options || [];
    var props = cartLine.properties || {};
    var selections = {};
    var topicSelections = {};
    var variantId = cartLine.variant_id;

    var map = parseShopifyBundleComponentsMap(props[PB_BUNDLE_COMPONENTS_KEY]);
    if (map && map.mode === 'mixed') {
      selections = hydrateShopifyBundleSelectionsFromMap(map, plan);
    }

    if (typeof pb.decodeCartLineToBuilderState === 'function') {
      var decoded = pb.decodeCartLineToBuilderState(cartLine, fields, { options: options });
      var decodedSelections =
        decoded && decoded.selections ? Object.assign({}, decoded.selections) : {};
      if (map && map.mode === 'mixed' && Object.keys(selections).length) {
        topicSelections =
          decoded && decoded.topicSelections ? Object.assign({}, decoded.topicSelections) : {};
      } else if (map && map.mode === 'mixed') {
        Object.keys(decodedSelections).forEach(function (key) {
          if (selections[key] == null || selections[key] === '') {
            selections[key] = decodedSelections[key];
          }
        });
        topicSelections =
          decoded && decoded.topicSelections ? Object.assign({}, decoded.topicSelections) : {};
      } else {
        selections = decodedSelections;
        topicSelections =
          decoded && decoded.topicSelections ? Object.assign({}, decoded.topicSelections) : {};
      }
      if (decoded && decoded.variantId != null) variantId = decoded.variantId;
    } else if (!Object.keys(selections).length && props && typeof props === 'object') {
      fields.forEach(function (field) {
        if (!field || !field.label) return;
        var mergeMode = plan.mode || 'pack';
        var visibleKey = resolveShopifyBundleVisiblePropertyKey(
          field,
          mergeMode,
          collectShopifyBundleCollidingLabels(fields),
          mainProduct.title
        );
        var selectionKey = resolveShopifyBundleFieldSelectionKey(field, mergeMode);
        if (props[visibleKey] != null) selections[selectionKey] = props[visibleKey];
        else if (props[field.label] != null) selections[selectionKey] = props[field.label];
      });
    }

    if (map && map.mode === 'mixed' && typeof pb.decodeTopicMultiProperties === 'function') {
      map.components.forEach(function(component) {
        var componentFields = fields.filter(function(field) {
          return field.provenance && String(field.provenance.productId) === String(component.productId);
        }).map(function(field) { return normalizeFieldForMount(field, 'mixed'); });
        pb.decodeTopicMultiProperties(component.properties, componentFields, selections, topicSelections, Object.assign({}, selections, component.properties));
      });
    }

    return {
      selections: selections,
      topicSelections: topicSelections,
      variantId: variantId,
    };
  }

  /**
   * Cart edit save — re-encode merged answers onto the same parent bundle line.
   * @param {object} params same as buildShopifyBundleMainCartItem
   * @returns {object|null} line properties
   */
  function encodeShopifyBundleCartEditProperties(params) {
    var row = buildShopifyBundleMainCartItem(params);
    if (!row || !row.properties) return null;
    return row.properties;
  }

  /**
   * Cart edit family save — single parent bundle line (+ caller adds addon rows).
   * @param {object} params
   * @param {object} params.pb
   * @param {object} params.builderState
   * @param {object} params.builderData
   * @param {object} [params.edit]
   * @param {string} [params.addonString]
   * @returns {object|null} { id, quantity, properties }
   */
  function buildShopifyBundleCartEditMainItem(params) {
    var edit = params && params.edit;
    var row = buildShopifyBundleMainCartItem(params);
    if (!row || row.id == null) return null;
    return {
      id: row.id,
      quantity: edit && edit.quantity != null ? edit.quantity : 1,
      properties: row.properties || {},
    };
  }

  function isShopifyBundleSingleParentCartPayload(items, builderData, builderState) {
    if (!isShopifyBundleMode(builderData)) return false;
    if (!Array.isArray(items) || items.length < 1) return false;
    var componentIds = collectShopifyBundleComponentProductIds(builderData);
    var mainVariant = builderState ? resolveShopifyBundleParentVariant(builderState) : null;
    var parentVariantId = mainVariant && mainVariant.id;
    var bundleLines = items.filter(function (row) {
      if (!row || row.id == null) return false;
      var props = row.properties || {};
      if (props['For Item'] != null && props['For Item'] !== '') return false;
      return true;
    });
    if (bundleLines.length !== 1) return false;
    var mainId = String(bundleLines[0].id);
    if (parentVariantId != null && String(parentVariantId) !== mainId) return false;
    return !componentIds.has(mainId);
  }

  function applyShopifyBundleOpenStep(pb) {
    var data = global.BUILDER_DATA;
    if (!isShopifyBundleMode(data)) return false;
    applyShopifyBundleMergeSync(false);
    var nav = global.PBNavigation;
    var navHelpers = nav
      ? {
          countVariantSteps: nav.countVariantSteps,
          hasAnyAddonsFromData: nav.hasAnyAddonsFromData
        }
      : null;
    var target = resolveShopifyBundleOpenStep(data, navHelpers);
    if (target <= 1) return false;
    var s = global.builderState;
    if (!s || !s.main) return false;
    s.main.step = target;
    if (pb && typeof pb.jumpToMainStep === 'function') {
      pb.jumpToMainStep(target);
    }
    return true;
  }

  /**
   * Eligibility + reason from BUILDER_DATA (snapshot-first). Debug via pb_debug_shopify_bundle_inherit.
   * @param {object} [data]
   * @returns {{ eligible: boolean, reason: string, mergedFieldCount: number, shopifyBundle: boolean }}
   */
  function resolveShopifyBundleEligibilityFromData(data) {
    data = data || global.BUILDER_DATA;
    var snap = data && data.shopifyBundleSnapshot;
    var components = data && Array.isArray(data.shopifyBundleComponents) ? data.shopifyBundleComponents : [];
    var fieldLists = components.map(function (c) {
      return c && Array.isArray(c.personalizationFields) ? c.personalizationFields : [];
    });
    var parentFields =
      data && data.mainProduct && Array.isArray(data.mainProduct.personalization)
        ? data.mainProduct.personalization
        : [];
    var hasAddons = !!(data && data.hasAddonStep);
    var hasComponentFields = fieldLists.some(function (list) {
      return list.length > 0;
    });
    var plan = buildShopifyBundleFieldPlan(components, parentFields);
    var mergedFieldCount = plan.fields.length;
    var shopifyBundle = isShopifyBundleMode(data);

    if (snap && snap.v === 1 && snap.shopifyBundle === true) {
      var snapCount = snap.mergedFieldCount != null ? Number(snap.mergedFieldCount) : 0;
      if (snapCount > 0) {
        return {
          eligible: true,
          reason: snap.eligibleReason || 'snapshot',
          mergedFieldCount: snapCount,
          shopifyBundle: true
        };
      }
      if (snap.eligible === true && !hasComponentFields) {
        return {
          eligible: true,
          reason: snap.eligibleReason || 'snapshot',
          mergedFieldCount: 0,
          shopifyBundle: true
        };
      }
    }

    if (shopifyBundle && (mergedFieldCount > 0 || hasComponentFields)) {
      var reason =
        parentFields.length > 0 && !hasComponentFields
          ? 'parent_personalization'
          : 'component_personalization';
      return {
        eligible: true,
        reason: reason,
        mergedFieldCount: mergedFieldCount,
        shopifyBundle: true
      };
    }

    if (hasAddons && shopifyBundle) {
      return {
        eligible: true,
        reason: 'parent_addons',
        mergedFieldCount: 0,
        shopifyBundle: true
      };
    }

    return {
      eligible: false,
      reason: 'none',
      mergedFieldCount: 0,
      shopifyBundle: shopifyBundle
    };
  }

  function debugShopifyBundleInherit(data) {
    if (!global.pb_debug_shopify_bundle_inherit) return;
    var result = resolveShopifyBundleEligibilityFromData(data);
    if (typeof console !== 'undefined' && console.info) {
      console.info('[pb-shopify-bundle-inherit]', result);
    }
  }

  /**
   * When Liquid cannot surface the large JSON snapshot metafield, fetch it from the worker.
   * @returns {Promise<object|null>}
   */
  function hydrateShopifyBundleSnapshotFromWorker() {
    var data = global.BUILDER_DATA;
    if (!data || data.shopifyBundle !== true) {
      return Promise.resolve(data || null);
    }
    var snap = data.shopifyBundleSnapshot;
    if (
      snap &&
      snap.v === 1 &&
      Array.isArray(snap.shopifyBundleComponents) &&
      snap.shopifyBundleComponents.length > 0
    ) {
      return Promise.resolve(data);
    }
    if (
      Array.isArray(data.shopifyBundleComponents) &&
      data.shopifyBundleComponents.length > 0
    ) {
      return Promise.resolve(data);
    }
    var base = data.shopifyBundleSnapshotFetchUrl;
    var handle =
      data.shopifyBundleSnapshotHandle ||
      (data.mainProduct && data.mainProduct.handle) ||
      '';
    if (!base || !handle) return Promise.resolve(data);
    var url = base + '?handle=' + encodeURIComponent(handle);
    return fetch(url, { credentials: 'omit', cache: 'no-store' })
      .then(function (res) {
        if (!res.ok) return data;
        return res.json();
      })
      .then(function (body) {
        if (!body || body.v !== 1) return data;
        data.shopifyBundleSnapshot = body;
        if (Array.isArray(body.shopifyBundleComponents)) {
          data.shopifyBundleComponents = body.shopifyBundleComponents;
        }
        return data;
      })
      .catch(function () {
        return data;
      });
  }

  function countVariantStepsFromData(data) {
    var nav = global.PBNavigation;
    if (nav && typeof nav.countVariantSteps === 'function') {
      return nav.countVariantSteps(data);
    }
    var options = (data && (data.optionsWithValues || data.options)) || [];
    if (!Array.isArray(options)) return 0;
    return options.filter(function (opt) {
      var name = String((opt && opt.name) || '').toLowerCase();
      var values = opt && opt.values;
      var size = Array.isArray(values) ? values.length : 0;
      return name !== 'title' && size > 0;
    }).length;
  }

  /**
   * Normalize snapshot / BUILDER_DATA field objects for data-pb-field-mount.
   * @param {object} field
   * @returns {object}
   */
  function isHiddenDerivedPropertiesField(field) {
    var type = String(field && field.type != null ? field.type : '').toLowerCase();
    var settings = field.settings;
    if (settings && typeof settings === 'object' && settings.value != null) settings = settings.value;
    var s = String(settings || '');
    var isDerivedType = type.indexOf('derived propert') !== -1;
    var isDeclarativeDerived =
      s.indexOf('first_initial_uppercase') !== -1 &&
      /sources\s*:/i.test(s) &&
      /outputs\s*:/i.test(s);
    if (!isDerivedType && !isDeclarativeDerived) return false;
    if (s.indexOf('"hidden":false') !== -1 || /hidden\s*:\s*false/i.test(s)) return false;
    return true;
  }

  function visiblePersonalizationFields(fields) {
    return (fields || []).filter(function (f) {
      return !isHiddenDerivedPropertiesField(f);
    });
  }

  function normalizeFieldForMount(field, mergeMode) {
    if (!field || field.label == null) return null;
    var settings = field.settings;
    if (settings && typeof settings === 'object' && settings.value != null) {
      settings = settings.value;
    }
    var mode = mergeMode || (field.provenance ? 'mixed' : 'pack');
    var displayLabel = field.label;
    var selectionKey = resolveShopifyBundleFieldSelectionKey(field, mode);
    var out = {
      label: selectionKey,
      question: field.question != null ? field.question : displayLabel,
      description: field.description != null ? String(field.description) : '',
      type: field.type != null ? field.type : 'text',
      settings: settings != null ? settings : {},
      visual: field.visual != null ? field.visual : field.visual_content != null ? field.visual_content : ''
    };
    if (field.provenance) out.provenance = field.provenance;
    if (displayLabel !== selectionKey) out.displayLabel = displayLabel;
    return out;
  }

  function waitForMainFlowDom(timeoutMs) {
    if (typeof document === 'undefined') return Promise.resolve(false);
    if (document.getElementById('main-flow-container')) return Promise.resolve(true);
    var deadline = Date.now() + (timeoutMs || 10000);
    return new Promise(function (resolve) {
      function tick() {
        if (document.getElementById('main-flow-container') || Date.now() >= deadline) {
          resolve(!!document.getElementById('main-flow-container'));
          return;
        }
        if (typeof requestAnimationFrame === 'function') requestAnimationFrame(tick);
        else setTimeout(tick, 16);
      }
      tick();
    });
  }

  function removeShopifyBundlePersonalizationSteps(viewport) {
    if (!viewport) return;
    Array.from(viewport.querySelectorAll('[data-pb-shopify-bundle-step="personalize"]')).forEach(function (step) {
      step.remove();
    });
  }

  function detachVariantAndTailSteps(viewport, variantCount) {
    var steps = Array.from(viewport.querySelectorAll('.pb-step'));
    var variants = steps.slice(0, variantCount);
    var tail = steps.slice(variantCount);
    variants.concat(tail).forEach(function (step) {
      step.remove();
    });
    return { variants: variants, tail: tail };
  }

  function renumberBodySteps(steps, startNum, kind, activeIndex) {
    steps.forEach(function (step, idx) {
      var stepNum = startNum + idx;
      step.id = (kind === 'image' ? 'img-page-' : 'content-page-') + stepNum;
      if (activeIndex === idx) {
        step.className = 'pb-step active';
      } else {
        step.className = 'pb-step';
      }
      var addonIdx = step.querySelector('#addon-step-index');
      if (addonIdx) addonIdx.value = String(stepNum);
    });
  }

  function collectTailSteps(viewport, bodyBlockCount) {
    if (!viewport) return [];
    var steps = Array.from(viewport.querySelectorAll('.pb-step'));
    return steps.filter(function (_step, idx) {
      return idx >= bodyBlockCount;
    });
  }

  function createPersonalizationStepElement(kind, stepNum, field, isActive, mergeMode) {
    var step = document.createElement('div');
    step.className = 'pb-step' + (isActive ? ' active' : '');
    step.id = (kind === 'image' ? 'img-page-' : 'content-page-') + stepNum;
    step.setAttribute('data-pb-shopify-bundle-step', 'personalize');
    /* PB-SBT-1 — scope for token lookup: `{Name}` resolves to this component's answer. */
    var stepProv = field && field.provenance;
    if (stepProv && stepProv.productId != null && String(stepProv.productId) !== '') {
      step.setAttribute('data-pb-scope-product-id', String(stepProv.productId));
    }

    if (kind === 'image') {
      var imgMount = document.createElement('div');
      imgMount.setAttribute('data-pb-field-mount-image', '');
      step.appendChild(imgMount);
      return step;
    }

    var mount = document.createElement('div');
    mount.setAttribute('data-pb-field-mount', '');
    var normalized = normalizeFieldForMount(field, mergeMode);
    if (!normalized) return step;
    mount.setAttribute('data-field', JSON.stringify(normalized));
    mount.setAttribute('data-pb-shopify-bundle-field-key', normalized.label);
    step.appendChild(mount);
    return step;
  }

  function reappendTailSteps(tailSteps, viewport, variantCount, p13nCount, kind) {
    tailSteps.forEach(function (step, i) {
      var newNum = p13nCount + variantCount + 1 + i;
      step.id = (kind === 'image' ? 'img-page-' : 'content-page-') + newNum;
      var addonIdx = step.querySelector('#addon-step-index');
      if (addonIdx) addonIdx.value = String(newNum);
      viewport.appendChild(step);
    });
  }

  /**
   * Mount merged personalization steps from BUILDER_DATA (PB-SBI-9).
   * Replaces Liquid component_reference walk for native Shopify bundles.
   *
   * @param {boolean} [forceRemount]
   * @returns {number} mounted field count
   */
  function mountShopifyBundlePersonalizationSteps(forceRemount) {
    if (typeof document === 'undefined') return 0;
    var data = global.BUILDER_DATA;
    if (!data || data.shopifyBundle !== true) return 0;
    if (!forceRemount && data._pbShopifyBundleStepsMounted) {
      return data._pbShopifyBundleStepsMountedCount || 0;
    }

    applyShopifyBundleMergeSync(false);
    var mergeMode = data.shopifyBundleMergeMode || 'pack';
    var fields = data.shopifyBundleMergedPersonalization;
    if (!Array.isArray(fields) && data.mainProduct) {
      fields = data.mainProduct.personalization || data.mainProduct.config || [];
    }
    if (!Array.isArray(fields)) fields = [];

    var root = document.getElementById('main-flow-container');
    if (!root) return 0;
    var imgVp = root.querySelector('.pb-modal-body .pb-step-viewport');
    var ctrlVp = root.querySelector('.pb-bottom-wrapper .pb-step-viewport');
    if (!imgVp || !ctrlVp) return 0;

    var variantCount = countVariantStepsFromData(data);
    removeShopifyBundlePersonalizationSteps(imgVp);
    removeShopifyBundlePersonalizationSteps(ctrlVp);
    var imgParts = detachVariantAndTailSteps(imgVp, variantCount);
    var ctrlParts = detachVariantAndTailSteps(ctrlVp, variantCount);

    var visibleFields = visiblePersonalizationFields(fields);
    var p13nCount = visibleFields.length;
    var firstActiveKind = p13nCount > 0 ? 'personalize' : variantCount > 0 ? 'variant' : null;

    visibleFields.forEach(function (field, idx) {
      var stepNum = idx + 1;
      imgVp.appendChild(
        createPersonalizationStepElement('image', stepNum, field, firstActiveKind === 'personalize' && idx === 0, mergeMode)
      );
      ctrlVp.appendChild(
        createPersonalizationStepElement('control', stepNum, field, firstActiveKind === 'personalize' && idx === 0, mergeMode)
      );
    });

    renumberBodySteps(
      imgParts.variants,
      p13nCount + 1,
      'image',
      firstActiveKind === 'variant' ? 0 : -1
    );
    renumberBodySteps(
      ctrlParts.variants,
      p13nCount + 1,
      'control',
      firstActiveKind === 'variant' ? 0 : -1
    );
    imgParts.variants.forEach(function (step) {
      imgVp.appendChild(step);
    });
    ctrlParts.variants.forEach(function (step) {
      ctrlVp.appendChild(step);
    });

    reappendTailSteps(imgParts.tail, imgVp, variantCount, p13nCount, 'image');
    reappendTailSteps(ctrlParts.tail, ctrlVp, variantCount, p13nCount, 'control');

    var pb = global.productBuilder;
    if (pb && typeof pb.mountRegistryMainFieldMounts === 'function') {
      pb.mountRegistryMainFieldMounts();
    } else if (pb && typeof pb.finalizeFieldPresentation === 'function') {
      pb.finalizeFieldPresentation('#main-flow-container');
    }
    if (pb && typeof pb.syncMainMaxStepsFromDom === 'function') {
      pb.syncMainMaxStepsFromDom();
    }

    data._pbShopifyBundleStepsMounted = true;
    data._pbShopifyBundleStepsMountedCount = fields.length;
    return fields.length;
  }

  function dispatchShopifyBundleReady(data) {
    data._pbShopifyBundleReady = true;
    if (typeof document === 'undefined') return;
    try {
      document.dispatchEvent(
        new CustomEvent('pb:shopify-bundle-ready', {
          detail: { mergedCount: countMergedPersonalizationFields(data) }
        })
      );
    } catch (e) {
      /* CustomEvent unavailable in some test harnesses */
    }
  }

  /**
   * Hydrate worker snapshot, merge fields, mount step DOM — gate Customize open (PB-SBI-9).
   * @returns {Promise<object|null>}
   */
  function ensureShopifyBundleReady() {
    var data = global.BUILDER_DATA;
    if (!data || data.shopifyBundle !== true) {
      return Promise.resolve(data || null);
    }
    if (data._pbShopifyBundleReady) {
      return Promise.resolve(data);
    }
    if (data._pbShopifyBundleReadyPromise) {
      return data._pbShopifyBundleReadyPromise;
    }

    data._pbShopifyBundleReadyPromise = hydrateShopifyBundleSnapshotFromWorker()
      .then(function () {
        applyShopifyBundleMergeSync(true);
        return waitForMainFlowDom();
      })
      .then(function (domReady) {
        if (domReady) mountShopifyBundlePersonalizationSteps(true);
        dispatchShopifyBundleReady(data);
        return data;
      })
      .catch(function () {
        applyShopifyBundleMergeSync(true);
        dispatchShopifyBundleReady(data);
        return data;
      });

    return data._pbShopifyBundleReadyPromise;
  }

  var api = {
    normalizeShopifyBundleFieldLabel: normalizeShopifyBundleFieldLabel,
    mergeShopifyBundlePersonalization: mergeShopifyBundlePersonalization,
    classifyShopifyBundleMergeMode: classifyShopifyBundleMergeMode,
    buildShopifyBundleFieldPlan: buildShopifyBundleFieldPlan,
    buildShopifyBundleAliasIndex: buildShopifyBundleAliasIndex,
    resolveShopifyBundleFieldSelectionKey: resolveShopifyBundleFieldSelectionKey,
    readShopifyBundleFieldSelectionValue: readShopifyBundleFieldSelectionValue,
    resolveShopifyBundleSelectionLookup: resolveShopifyBundleSelectionLookup,
    resolveShopifyBundleFieldIdentityKeys: resolveShopifyBundleFieldIdentityKeys,
    resolveShopifyBundleMediaSelectionLookup: resolveShopifyBundleMediaSelectionLookup,
    collectShopifyBundleCollidingLabels: collectShopifyBundleCollidingLabels,
    resolveShopifyBundleVisiblePropertyKey: resolveShopifyBundleVisiblePropertyKey,
    parseShopifyBundleComponentsMap: parseShopifyBundleComponentsMap,
    encodeShopifyBundleComponentsMap: encodeShopifyBundleComponentsMap,
    hydrateShopifyBundleSelectionsFromMap: hydrateShopifyBundleSelectionsFromMap,
    resolveShopifyBundleFieldPlanFromData: resolveShopifyBundleFieldPlanFromData,
    isShopifyBundleProductShape: isShopifyBundleProductShape,
    applyShopifyBundleMergeSync: applyShopifyBundleMergeSync,
    isShopifyBundleMode: isShopifyBundleMode,
    countMergedPersonalizationFields: countMergedPersonalizationFields,
    buildShopifyBundleStepKinds: buildShopifyBundleStepKinds,
    resolveShopifyBundleOpenStep: resolveShopifyBundleOpenStep,
    applyShopifyBundleOpenStep: applyShopifyBundleOpenStep,
    resolveShopifyBundleParentVariant: resolveShopifyBundleParentVariant,
    collectShopifyBundleComponentProductIds: collectShopifyBundleComponentProductIds,
    buildShopifyBundleMainCartItem: buildShopifyBundleMainCartItem,
    isShopifyBundleSingleParentCartPayload: isShopifyBundleSingleParentCartPayload,
    prepareShopifyBundleCartEditState: prepareShopifyBundleCartEditState,
    hydrateShopifyBundleCartEditFromLine: hydrateShopifyBundleCartEditFromLine,
    encodeShopifyBundleCartEditProperties: encodeShopifyBundleCartEditProperties,
    buildShopifyBundleCartEditMainItem: buildShopifyBundleCartEditMainItem,
    resolveShopifyBundleEligibilityFromData: resolveShopifyBundleEligibilityFromData,
    hydrateShopifyBundleSnapshotFromWorker: hydrateShopifyBundleSnapshotFromWorker,
    ensureShopifyBundleReady: ensureShopifyBundleReady,
    mountShopifyBundlePersonalizationSteps: mountShopifyBundlePersonalizationSteps,
    normalizeFieldForMount: normalizeFieldForMount,
    countVariantStepsFromData: countVariantStepsFromData,
    debugShopifyBundleInherit: debugShopifyBundleInherit
  };

  global.PBShopifyBundleInherit = api;

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = api;
  }
})(typeof window !== 'undefined' ? window : globalThis);
